import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPromotion, endPromotion, getPromotions } from 'API/engagement';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EngagementPromotion, PromotionPreview } from 'types/engagement.types';
import { fmtEuroCents, fmtInt, fmtPct } from '../../shared/format';
import { QueryState, SectionCard } from '../../shared/ui';
import AudienceEditor from '../audience-editor';
import { promotionDraftValid, type PromotionDraft as Draft } from '../engagement-logic';

const PRESETS = [
  { key: 'p20x3', percentOff: 20, durationMonths: 3 },
  { key: 'p33x3', percentOff: 33, durationMonths: 3 },
  // The API caps discounts at 60 % (no free months via promotions).
  { key: 'p50x1', percentOff: 50, durationMonths: 1 },
];

const initialDraft = (): Draft => ({
  name: '',
  discount: { percentOff: 20, durationMonths: 3 },
  audience: { kind: 'trial', trialDayFrom: 20, minReplies: 1, requireConsent: 'marketing-email' },
  endsAfterDays: 7,
  maxRedemptions: 30,
  controlGroupPercent: 10,
});

const PreviewSummary: React.FC<{ preview: PromotionPreview }> = ({ preview }) => {
  const { t } = useTranslation();
  const p = preview.preview;
  const est = p?.estimate;
  // Expected control group: the share of the estimated recipients that gets no discount.
  const controlSize = est && p ? Math.round((est.targeted * p.controlGroupPercent) / 100) : undefined;
  return (
    <Alert severity={'info'} data-testid={'promotion-preview'}>
      <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{t('engPromoPreviewTitle')}</Typography>
      {p?.text ? <Typography sx={{ fontSize: 13 }}>{p.text}</Typography> : null}
      {p?.code ? <Typography sx={{ fontSize: 13 }}>Code: {p.code}</Typography> : null}
      {est ? <Typography sx={{ fontSize: 13 }}>{t('engEstimateText', { targeted: est.targeted, withEmail: est.withEmail })}</Typography> : null}
      {!est && p?.estimateReason ? <Typography sx={{ fontSize: 13 }}>{p.estimateReason}</Typography> : null}
      {typeof controlSize === 'number' && controlSize > 0 ? (
        <Typography sx={{ fontSize: 13 }}>{t('engPromoControlSize', { count: controlSize })}</Typography>
      ) : null}
      <Typography sx={{ fontSize: 12, mt: 0.5 }}>{t('engPromoPreviewNothingCreated')}</Typography>
    </Alert>
  );
};

const PromotionsTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(initialDraft);
  const [preview, setPreview] = useState<{ draft: string; data: PromotionPreview } | null>(null);
  const [message, setMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);

  const promotions = useQuery({ queryKey: ['engagement', 'promotions'], queryFn: getPromotions });
  const draftKey = JSON.stringify(draft);
  const previewValid = preview?.draft === draftKey;

  const dry = useMutation({
    mutationFn: () => createPromotion({ ...draft, dryRun: true }),
    onSuccess: (data) => {
      setPreview({ draft: draftKey, data });
      setMessage(null);
    },
    onError: (e) => setMessage({ severity: 'error', text: (e as Error).message }),
  });
  const confirm = useMutation({
    mutationFn: () => createPromotion({ ...draft, dryRun: false }),
    onSuccess: () => {
      setMessage({ severity: 'success', text: t('engPromoCreated') });
      setPreview(null);
      setDraft(initialDraft());
      queryClient.invalidateQueries({ queryKey: ['engagement', 'promotions'] });
    },
    onError: (e) => setMessage({ severity: 'error', text: (e as Error).message }),
  });
  const end = useMutation({
    mutationFn: endPromotion,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['engagement', 'promotions'] }),
  });

  const presetKey = PRESETS.find((p) => p.percentOff === draft.discount.percentOff && p.durationMonths === draft.discount.durationMonths)?.key ?? 'custom';
  const num = (v: string) => (v === '' ? 0 : Number(v));

  const list = promotions.data ?? [];
  const control = list.reduce(
    (acc, p) => ({ size: acc.size + (p.stats?.control?.size ?? 0), purchased: acc.purchased + (p.stats?.control?.purchased ?? 0) }),
    { size: 0, purchased: 0 }
  );

  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} gap={2}>
      <Box sx={{ flex: 2, minWidth: 0 }}>
        <SectionCard title={t('engPromoNew')}>
          <Stack gap={2}>
            <TextField size={'small'} label={t('engPromoName')} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <TextField
              select
              size={'small'}
              label={t('engPromoDiscount')}
              value={presetKey}
              onChange={(e) => {
                const p = PRESETS.find((x) => x.key === e.target.value);
                if (p) setDraft({ ...draft, discount: { percentOff: p.percentOff, durationMonths: p.durationMonths } });
              }}
            >
              {PRESETS.map((p) => (
                <MenuItem key={p.key} value={p.key}>
                  {t('engPromoDiscountLabel', { percent: p.percentOff, months: p.durationMonths })}
                </MenuItem>
              ))}
              <MenuItem value={'custom'}>{t('engPromoCustom')}</MenuItem>
            </TextField>
            <Stack direction={'row'} gap={1.5}>
              <TextField
                size={'small'}
                type={'number'}
                label={t('engPromoPercent')}
                value={draft.discount.percentOff}
                onChange={(e) => setDraft({ ...draft, discount: { ...draft.discount, percentOff: num(e.target.value) } })}
              />
              <TextField
                size={'small'}
                type={'number'}
                label={t('engPromoMonths')}
                value={draft.discount.durationMonths}
                onChange={(e) => setDraft({ ...draft, discount: { ...draft.discount, durationMonths: num(e.target.value) } })}
              />
            </Stack>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>{t('engPromoAudience')}</Typography>
              <AudienceEditor value={draft.audience} onChange={(a) => setDraft({ ...draft, audience: a })} />
            </Box>
            <Stack direction={'row'} gap={1.5} flexWrap={'wrap'}>
              <TextField
                size={'small'}
                type={'number'}
                label={t('engPromoEndsAfter')}
                helperText={t('engPromoEndsAfterHelp')}
                value={draft.endsAfterDays}
                onChange={(e) => setDraft({ ...draft, endsAfterDays: num(e.target.value) })}
              />
              <TextField
                size={'small'}
                type={'number'}
                label={t('engPromoMaxRedemptions')}
                value={draft.maxRedemptions}
                onChange={(e) => setDraft({ ...draft, maxRedemptions: num(e.target.value) })}
              />
              <TextField
                size={'small'}
                type={'number'}
                label={t('engPromoControl')}
                helperText={t('engPromoControlHelp')}
                value={draft.controlGroupPercent}
                onChange={(e) => setDraft({ ...draft, controlGroupPercent: num(e.target.value) })}
              />
            </Stack>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('engPromoStripeNote')}</Typography>
            <Stack direction={'row'} gap={1.5}>
              <Button variant={'outlined'} disabled={!promotionDraftValid(draft) || dry.isPending} onClick={() => dry.mutate()} data-testid={'promo-preview'}>
                {t('engPromoPreview')}
              </Button>
              <Button variant={'contained'} disabled={!previewValid || confirm.isPending} onClick={() => confirm.mutate()} data-testid={'promo-confirm'}>
                {t('engPromoConfirm')}
              </Button>
            </Stack>
            {preview && previewValid ? <PreviewSummary preview={preview.data} /> : null}
            {preview && !previewValid ? <Typography sx={{ fontSize: 12, color: 'warning.dark' }}>{t('engPromoPreviewStale')}</Typography> : null}
            {message ? <Alert severity={message.severity}>{message.text}</Alert> : null}
          </Stack>
        </SectionCard>
      </Box>
      <Box sx={{ flex: 3, minWidth: 0 }}>
        <SectionCard title={t('engPromoList')}>
          <QueryState isLoading={promotions.isLoading} error={promotions.error} isEmpty={list.length === 0} emptyText={t('engPromoEmpty')}>
            <Table size={'small'}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('engPromoName')}</TableCell>
                  <TableCell>{t('engColStatus')}</TableCell>
                  <TableCell align={'right'}>{t('engColDelivered')}</TableCell>
                  <TableCell align={'right'}>{t('engPromoRedeemed')}</TableCell>
                  <TableCell align={'right'}>{t('engPromoRevenue')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((p: EngagementPromotion) => (
                  <TableRow key={p._id}>
                    <TableCell>
                      {p.name}
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                        {t('engPromoDiscountLabel', { percent: p.discount.percentOff, months: p.discount.durationMonths })} · {t('engPromoCap', { count: p.maxRedemptions })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size={'small'} label={t(`engPromoStatus_${p.status}`)} color={p.status === 'running' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align={'right'}>{p.stats ? fmtInt(lng)(p.stats.delivered) : '—'}</TableCell>
                    <TableCell align={'right'}>{p.stats ? fmtInt(lng)(p.stats.redeemed) : '—'}</TableCell>
                    <TableCell align={'right'}>{p.stats ? fmtEuroCents(lng)(p.stats.revenueCents) : '—'}</TableCell>
                    <TableCell>
                      {p.status === 'running' ? (
                        <Button size={'small'} color={'error'} disabled={end.isPending} onClick={() => end.mutate(p._id)}>
                          {t('engPromoEnd')}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>
                    <em>{t('engPromoControlRow')}</em>
                  </TableCell>
                  <TableCell>
                    <Chip size={'small'} variant={'outlined'} label={t('engPromoControlGroup')} />
                  </TableCell>
                  <TableCell align={'right'}>{fmtInt(lng)(control.size)}</TableCell>
                  <TableCell align={'right'}>{fmtInt(lng)(control.purchased)}</TableCell>
                  <TableCell align={'right'}>{control.size ? fmtPct(lng, 1)(control.purchased / control.size) : '—'}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>{t('engPromoControlNote')}</Typography>
          </QueryState>
        </SectionCard>
      </Box>
    </Stack>
  );
};

export default PromotionsTab;
